import { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, ButtonGroup, Can, Icon } from '@iq/react-components';
import { useSelector } from 'react-redux';
import { HvTabs, HvTab } from '@hitachivantara/uikit-react-core';

import { EVENT_TYPE_USER, EVENT_TYPE_DTMP, EVENT_TYPE_MACH } from '../../../constants';
import EventFilters from './EventFilters';

import ExportEventModal from './ExportEventModal/ExportEventModal';
import { getActiveSite } from '../../../bundles/sites';

const EventTimelineHeader = ({
  format,
  layouts = [],
  activeLayout,
  onSetLayout,
  onCreateEvent,
  width,
  isFullScreen,
  panelId,
  availableIotEventTypes,
  availableUserEventTypes,
  allUserEventTypes,
  onEventTypeChange,
  selectedEventType,
  enabledEventTypes = [],
}) => {
  const site = useSelector(getActiveSite);
  const [showExportEventModal, setShowExportEventModal] = useState(false);

  const eventTimelineTabs = useMemo(
    () =>
      [
        {
          eventType: EVENT_TYPE_USER,
          label: 'User Events',
          id: 'user-events',
        },
        {
          eventType: EVENT_TYPE_DTMP,
          label: 'DTMP IoT Events',
          id: 'dtmp-iot-events',
        },
        {
          eventType: EVENT_TYPE_MACH,
          label: 'Mach IoT Events',
          id: 'mach-iot-events',
        },
      ].filter((tab) => enabledEventTypes.includes(tab.eventType)),
    [enabledEventTypes]
  );
  const [tabIndex, setTabIndex] = useState(() => {
    const initialSelectedTabIndex = eventTimelineTabs.findIndex(
      (tab) => tab.eventType === selectedEventType
    );

    return initialSelectedTabIndex === -1 ? 0 : initialSelectedTabIndex;
  });

  useEffect(() => {
    const selectedTabIndex = eventTimelineTabs.findIndex(
      (tab) => tab.eventType === selectedEventType
    );

    if (selectedTabIndex !== -1) {
      setTabIndex(selectedTabIndex);
      return;
    }

    const fallbackTab = eventTimelineTabs[0];

    if (fallbackTab) {
      setTabIndex(0);
      onEventTypeChange(fallbackTab.eventType);
    }
  }, [eventTimelineTabs, onEventTypeChange, selectedEventType]);

  const handleTabChange = (_event, newTabIndex) => {
    setTabIndex(newTabIndex);
    const selectedTab = eventTimelineTabs[newTabIndex];

    if (selectedTab) {
      onEventTypeChange(selectedTab.eventType);
    }
  };

  // Removes limited list view from the button group, since this should not be an option for the user to select in the event timeline header.
  const filteredLayouts = layouts.filter((layout) => layout.type !== 'limited list view');
  const hasAvailableEventTypes =
    availableIotEventTypes?.mach?.length > 0 ||
    availableIotEventTypes?.dtmp?.eventTypes?.length > 0 ||
    availableUserEventTypes.length > 0;

  const onClose = useCallback(() => {
    setShowExportEventModal(false);
  }, []);

  if (format === 'narrow') return null;

  return (
    <div
      className={`event-timeline-panel-header ${format} ${
        isFullScreen ? 'fullscreen' : 'normalscreen'
      } `}
    >
      <div className="event-timeline-panel-toolbar">
        <div className="event-timeline-panel-btn-wrapper">
          {layouts.length > 1 && (
            <ButtonGroup
              layouts={filteredLayouts}
              activeLayout={activeLayout}
              onClick={onSetLayout}
            />
          )}
          <Button
            icon={
              <Icon
                style={{ width: '85%' }}
                icon="he-download"
              />
            }
            activity="secondary"
            onClick={() => setShowExportEventModal(!showExportEventModal)}
            tooltip={'Export events'}
            disabled={!hasAvailableEventTypes}
          ></Button>
          <Can
            permission="events/Write"
            scope={{ org: site.org, site: site.id }}
          >
            {selectedEventType === EVENT_TYPE_USER && (
              <Button
                activity="primary"
                onClick={onCreateEvent}
                tooltip={width < 800 && 'Create new'}
                disabled={!hasAvailableEventTypes}
              >
                <div className="button-text">Add event</div>
              </Button>
            )}
          </Can>
        </div>

        <EventFilters
          panelId={panelId}
          availableIotEventTypes={availableIotEventTypes}
          availableUserEventTypes={availableUserEventTypes}
          site={site}
        />
      </div>

      {enabledEventTypes.length > 1 && (
        <div className="event-timeline-panel-tabs">
          <HvTabs
            id="event-timeline-tabs"
            value={tabIndex}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            {eventTimelineTabs.map((tab) => (
              <HvTab
                key={tab.id}
                id={tab.id}
                label={tab.label}
              />
            ))}
          </HvTabs>
        </div>
      )}

      {showExportEventModal && (
        <ExportEventModal
          onClose={onClose}
          panelId={panelId}
          site={site}
          availableIotEventTypes={availableIotEventTypes}
          availableUserEventTypes={availableUserEventTypes}
          allUserEventTypes={allUserEventTypes}
        />
      )}
    </div>
  );
};

export default EventTimelineHeader;
